import React, { Fragment } from 'react'
import { Controller } from 'react-hook-form'
import type { Control, FieldErrors } from 'react-hook-form'
import { Input, Label, TrashIcon } from '@quickmd-llc/design-system'
import type { MedicationFormValues } from '../../types'

interface PricingTierRowProps {
  index: number
  control: Control<MedicationFormValues>
  errors: FieldErrors<MedicationFormValues>
  onRemove: () => void
  showRemove: boolean
  touchedFields: Partial<Record<keyof MedicationFormValues, unknown>>
  isSubmitted: boolean
}

export function PricingTierRow({
  index,
  control,
  errors,
  onRemove,
  showRemove,
  touchedFields,
  isSubmitted,
}: PricingTierRowProps) {
  const tierErrors = errors.pricingTiers?.[index]
  const tierTouched = (touchedFields.pricingTiers as Array<Record<string, unknown>> | undefined)?.[index]

  const showQuantityError = (tierTouched?.quantity || isSubmitted) && tierErrors?.quantity
  const showPriceError = (tierTouched?.patientPrice || isSubmitted) && tierErrors?.patientPrice

  return (
    <Fragment>
      {/* col 1: quantity */}
      <div>
        <Controller
          control={control}
          name={`pricingTiers.${index}.quantity`}
          render={({ field: { value, onChange, onBlur } }) => (
            <Input
              value={typeof value === 'number' && !isNaN(value) ? value : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const raw = e.target.value
                if (raw !== '' && /\.\d{3,}/.test(raw)) return
                onChange(e.target.valueAsNumber)
              }}
              onBlur={onBlur}
              type='number'
              min={0}
              step={0.01}
              placeholder='30'
              error={Boolean(showQuantityError)}
              aria-label='Quantity'
            />
          )}
        />
        {showQuantityError ?
          <Label size='sm' className='mt-sp-0.5 text-text-error'>
            {tierErrors?.quantity?.message}
          </Label>
        : null}
      </div>

      {/* col 2: price + trash inside the same column so it doesn't shift the grid */}
      <div className='flex items-start gap-sp-1'>
        <div className='flex-1'>
          <Controller
            control={control}
            name={`pricingTiers.${index}.patientPrice`}
            render={({ field: { value, onChange, onBlur } }) => (
              <Input
                value={typeof value === 'number' && !isNaN(value) ? value : ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = e.target.value
                  if (raw !== '' && /\.\d{3,}/.test(raw)) return
                  onChange(e.target.valueAsNumber)
                }}
                onBlur={onBlur}
                type='number'
                min={0}
                step={0.01}
                placeholder='0.00'
                leftSideLabel='$'
                error={Boolean(showPriceError)}
                aria-label='Price'
              />
            )}
          />
          {showPriceError ?
            <Label size='sm' className='mt-sp-0.5 text-text-error'>
              {tierErrors?.patientPrice?.message}
            </Label>
          : null}
        </div>
        <button
          type='button'
          onClick={onRemove}
          disabled={!showRemove}
          className='mt-sp-1.5 text-text-error transition-colors hover:text-text-error disabled:cursor-not-allowed disabled:opacity-40'
          aria-label='Remove pricing tier'>
          <TrashIcon iconSize='s' className='text-current' />
        </button>
      </div>
    </Fragment>
  )
}
